import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { ILink, WithLinks } from '../../caremap/WithLinks';
import { ICareMap } from '../../caremap/ICareMap';
import { ICareMapNoteTarget } from './ICareMapNoteTarget';
import { ILightUser } from '../../user/UserState';
import { IRecording } from '../../recording/RecordingState';
import { IAttachment } from '../generic-notes/attachments/IAttachment';

const careMapNoteMediaType = 'application/vnd.innerhive.caremap-note+json';
const careMapNoteHeaders = { accept: careMapNoteMediaType, 'content-type': careMapNoteMediaType };

export interface INoteReactions {
    [reaction: string]: {
        people: {
            name: string;
            isMe: boolean;
        }[];
    }
}

type ISO8601 = string;
export interface ICaremapNoteResponse extends ICaremapNoteRequest {
    createdAt: ISO8601
    createdBy: string;
    createdByUser?: ILightUser;
    updatedAt: ISO8601
    updatedBy: string;
    version: number;
    links: any;
    reactions: INoteReactions;
    isBeaCommentSeen?: boolean;
}

interface ICaremapNoteRequest {
    noteId: string;
    html: string;
    isPrivate?: boolean;
    title: string;
    attachments: IAttachment[];
    tags?: string[];

    otherAttributes: {
        // careMapKey also lives in here but that is managed by (and ignored by) the api
        noteTarget: ICareMapNoteTarget;
        parentNoteId?: string;
        recording?: Partial<IRecording>;
    }

    version?: number;
}

export type ICaremapNote = ICaremapNoteRequest & Partial<ICaremapNoteResponse>;

export class NotesClient {
    static inject = () => [APIClient];
    constructor(private apiClient: APIClient){}

    getNotesForCareMap = async (careMap: ICareMap): Promise<WithLinks<ICaremapNoteResponse>[]> => {
        return await this.apiClient.get(careMap.links.notes, careMapNoteMediaType);
    };

    getCommentsForNote = async (parentNote: { links?: { comments?: ILink } }): Promise<WithLinks<ICaremapNoteResponse>[]> => {
        if (!parentNote.links?.comments) {
            throw new Error('parent note has no comments link');
        }
        return await this.apiClient.get(parentNote.links.comments, careMapNoteMediaType);
    };

    getNote = async (selfLink: ILink): Promise<WithLinks<ICaremapNoteResponse>> => {
        return await this.apiClient.get(selfLink, careMapNoteMediaType);
    };

    // For callers that hold only a note id, such as a deep link or a push notification, and so
    // have no self link to follow.
    getNoteById = async ({ careMap, noteId }: { careMap: ICareMap; noteId: string }): Promise<WithLinks<ICaremapNoteResponse>> => {
        return await this.apiClient.get(careMap.links.note, careMapNoteMediaType, { noteId });
    };

    addNoteToCareMap = async ({ careMap, note }: { careMap: ICareMap; note: ICaremapNoteRequest & { isComplete?: boolean }; }): Promise<WithLinks<ICaremapNoteResponse>> => {
        return await this.apiClient.post(careMap.links.notes, note, careMapNoteHeaders);
    };

    addCommentToNote = async ({ parentNote, comment }: { parentNote: { links?: { comments?: ILink } }; comment: ICaremapNoteRequest; }): Promise<WithLinks<ICaremapNoteResponse>> => {
        if (!parentNote.links?.comments) {
            throw new Error('parent note has no comments link');
        }
        return await this.apiClient.post(parentNote.links.comments, comment, careMapNoteHeaders);
    };

    updateNote = async (note: WithLinks<ICaremapNoteRequest>) => {
        return await this.apiClient.put(note.links.self, note, careMapNoteHeaders);
    };

    deleteNote = async (note: WithLinks<ICaremapNoteRequest>) => {
        await this.apiClient.delete(note.links.self, {});
    };

    putSummaryRating = async (note: WithLinks<ICaremapNoteRequest>, summaryRating: 'positive' | 'negative') => {
        return await this.apiClient.put(
            note.links.summaryRating,
            { summaryRating },
            careMapNoteHeaders
        );
    };

    getNoteReactions = async (note: WithLinks<ICaremapNoteRequest>) => {
        const { reactions } = await this.apiClient.get(note.links.reactions);
        return reactions;
    };

    markBeaCommentSeen = async (note: ICaremapNoteResponse): Promise<void> => {
        await this.apiClient.put(note.links.beaCommentSeen, {});
    };
}
